import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import SEOHead from './SEOHead';
import GoogleReviewsWidget from './GoogleReviewsWidget';
import { 
  Crown, 
  ArrowRight, 
  Monitor, 
  Cloud, 
  Shield, 
  Server, 
  Headphones, 
  HardDrive,
  CheckCircle,
  Star,
  Users,
  Clock,
  Award,
  Phone,
  Gift,
  Zap,
  Lock,
  Globe,
  Settings,
  Database,
  Wifi,
  MessageSquarePlus,
  X,
  Loader2,
  Scissors,
  TrendingDown
} from 'lucide-react';

// Cost Cutter Banner - NEW SERVICE
const CostCutterBanner = () => {
  return (
    <Link to="/cost-cutter" className="block">
      <div className="relative bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 overflow-hidden">
        {/* Animated dollar signs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/5 text-2xl text-black/10 animate-bounce" style={{ animationDelay: '0s' }}>$</div>
          <div className="absolute top-1/2 left-2/3 text-xl text-black/10 animate-bounce" style={{ animationDelay: '0.3s' }}>$</div>
          <div className="absolute top-3/4 left-1/3 text-2xl text-black/10 animate-bounce" style={{ animationDelay: '0.6s' }}>$</div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-3 sm:gap-6">
          <div className="flex items-center gap-2 text-black">
            <Scissors className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="font-bold text-sm sm:text-base uppercase tracking-wide px-2 py-0.5 bg-black/20 rounded">NEW</span>
          </div>
          
          <div className="text-black font-semibold text-sm sm:text-lg text-center flex items-center gap-2">
            <span className="font-black">COST CUTTER</span>
            <span className="hidden sm:inline">- Save 20-40% on ALL Vendor Expenses</span>
          </div>
          
          <div className="hidden sm:flex items-center gap-2 bg-white text-black px-4 py-1.5 rounded-full font-bold text-sm hover:bg-gray-100 transition-colors">
            <TrendingDown className="w-4 h-4" />
            Free Consultation
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
};

// Giveaway Banner
const GiveawayBanner = () => {
  return (
    <Link to="/giveaway" className="block">
      <div className="relative bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 overflow-hidden animate-pulse-slow">
        {/* Sparkle effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDelay: '0s' }} />
          <div className="absolute top-1/3 left-1/2 w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDelay: '0.5s' }} />
          <div className="absolute top-2/3 left-3/4 w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-3 sm:gap-6">
          <div className="flex items-center gap-2 text-black">
            <Gift className="w-5 h-5 sm:w-6 sm:h-6 animate-bounce" />
            <span className="font-bold text-sm sm:text-base uppercase tracking-wide">🎉 GIVEAWAY!</span>
          </div>
          
          <div className="text-black font-semibold text-sm sm:text-lg text-center">
            <span className="font-black">Monthly Giveaway</span>
          </div>
          
          <div className="hidden sm:flex items-center gap-2 bg-black text-amber-400 px-4 py-1.5 rounded-full font-bold text-sm hover:bg-gray-900 transition-colors">
            <Zap className="w-4 h-4" />
            Enter Now
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative bg-gradient-hero text-white overflow-hidden" aria-labelledby="hero-heading">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/50 rounded-full blur-3xl" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6 lg:space-y-8 animate-fade-in-up order-2 lg:order-1">
            <Badge className="bg-primary/20 text-primary border-primary/30 px-4 py-1.5">
              <Crown className="w-4 h-4 mr-2" />
              Toronto IT Support & Managed Services
            </Badge>
            
            <h1 id="hero-heading" className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-tight">
              IT Support &{' '}
              <span className="text-gradient-gold">Managed IT Services in Toronto</span>
            </h1>
            
            <p className="text-base sm:text-lg text-gray-300 max-w-xl leading-relaxed">
              King of IT is a Toronto-based IT services company providing <strong>managed IT</strong>, <strong>cybersecurity</strong>, <strong>Microsoft 365</strong>, <strong>cloud</strong>, <strong>network</strong>, and <strong>onsite IT support</strong> to small and mid-sized businesses throughout Toronto and the GTA. With 31 years in the IT industry, we serve 500+ clients. <strong>Call 647-951-3653</strong> for same-day support.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/contact" data-testid="hero-cta-contact">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90 group"
                >
                  Get Free IT Consultation
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="tel:6479513653" data-testid="hero-cta-phone">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  647-951-3653
                </Button>
              </a>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-8 pt-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span className="text-sm text-gray-300">24/7 Emergency Support</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span className="text-sm text-gray-300">Same-Day Service</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span className="text-sm text-gray-300">On-Site & Remote</span>
              </div>
            </div>
          </div>
          
          {/* Right Content - Image */}
          <div className="relative animate-fade-in order-1 lg:order-2" style={{ animationDelay: '200ms' }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/optimized/pexels-5380642-sm.webp"
                srcSet="/images/optimized/pexels-5380642-xs.webp 400w, /images/optimized/pexels-5380642-sm.webp 600w"
                sizes="(max-width: 500px) 400px, 600px"
                width="600"
                height="400"
                alt="King of IT technician providing professional IT support and computer repair services in Toronto data center"
                className="w-full h-64 sm:h-80 lg:h-auto object-cover"
                loading="eager"
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent" />
            </div>
            
            {/* Floating Card */}
            <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-white rounded-xl p-3 sm:p-4 shadow-xl animate-float hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-gold rounded-full flex items-center justify-center">
                  <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-secondary text-sm sm:text-base">King of IT</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">31 Years Trusted</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Monitor,
      title: 'IT Consulting & Support',
      description: 'Expert IT consultants providing strategic guidance and emergency support for Toronto businesses. We analyze your systems and create custom solutions that fit your budget.',
      link: '/services#consulting'
    },
    {
      icon: Headphones,
      title: 'Remote IT Support',
      description: 'Location-independent IT support for remote teams in the GTA. Our engineers resolve tech issues quickly regardless of where your team is located.',
      link: '/services#remote'
    },
    {
      icon: Server,
      title: 'Managed IT Services',
      description: 'Comprehensive IT management including 24/7 network monitoring, maintenance, and proactive issue prevention to keep your Toronto business running.',
      link: '/services#managed'
    },
    {
      icon: Shield,
      title: 'Business Continuity & Cybersecurity',
      description: 'Disaster recovery planning, backup solutions, and cybersecurity services to protect your business from threats and ensure quick recovery from any IT emergency.',
      link: '/services#continuity'
    },
    {
      icon: Cloud,
      title: 'Cloud Services & Migration',
      description: 'Full cloud solutions including Microsoft 365, Google Workspace, AWS setup, and enterprise-level cloud migration for businesses in Toronto and the GTA.',
      link: '/services#outsourced'
    },
    {
      icon: HardDrive,
      title: 'Computer Repair & Hardware',
      description: 'Professional computer repair services and quality hardware solutions including new and certified refurbished equipment for homes and businesses in Toronto.',
      link: '/services#hardware'
    }
  ];

  return (
    <section className="py-16 lg:py-28 bg-background" id="services" aria-labelledby="services-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            IT Services Toronto
          </Badge>
          <h2 id="services-heading" className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-foreground mb-4">
            Professional IT Services for <span className="text-gradient-gold">Toronto Businesses</span>
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg">
            From small startups to enterprise organizations across the Greater Toronto Area, we provide comprehensive IT solutions including computer repair, network setup, cybersecurity, and managed services.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {services.map((service, index) => (
            <Link to={service.link} key={service.title} data-testid={`service-card-${index}`}>
              <Card 
                className="group p-5 sm:p-6 lg:p-8 hover:shadow-card-hover hover-lift border-border/50 bg-card h-full cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-gold flex items-center justify-center shadow-gold group-hover:scale-110 transition-transform">
                    <service.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                </div>
                <h3 className="font-display font-semibold text-lg sm:text-xl text-foreground mb-2 sm:mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed">
                  {service.description}
                </p>
                <span className="inline-flex items-center text-primary font-medium hover:gap-3 gap-2 transition-all text-sm sm:text-base">
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Card>
            </Link>
          ))}
        </div>
        
        {/* View All Services Button */}
        <div className="text-center mt-10 lg:mt-12">
          <Link to="/services" data-testid="view-all-services-btn">
            <Button size="lg" className="bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
              View All IT Services
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { icon: Users, value: '500+', label: 'Happy Clients in Toronto' },
    { icon: Clock, value: '31', label: 'Years Serving GTA' },
    { icon: Award, value: '10K+', label: 'Issues Resolved/Year' },
    { icon: Star, value: '100%', label: 'Client Satisfaction' }
  ];

  return (
    <section className="py-12 lg:py-16 bg-gradient-navy text-white" aria-label="Company Statistics">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat, index) => (
            <div key={stat.label} className="text-center" data-testid={`stat-${index}`}>
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full bg-white/10 flex items-center justify-center">
                <stat.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <p className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-primary mb-1 sm:mb-2">
                {stat.value}
              </p>
              <p className="text-gray-300 text-sm sm:text-base">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const benefits = [
    {
      icon: Zap,
      title: 'Same-Day Response',
      description: 'IT issues can\'t wait. Our Toronto-based team responds quickly to minimize downtime and keep your business running smoothly.'
    },
    {
      icon: Lock,
      title: 'Cybersecurity Experts',
      description: 'Protect your business from cyber threats with industry-best security practices and 24/7 monitoring across the GTA.'
    },
    {
      icon: Globe,
      title: 'GTA-Wide Coverage',
      description: 'On-site and remote IT support throughout Toronto, Markham, Vaughan, Mississauga, Brampton, and Richmond Hill.'
    },
    {
      icon: Settings,
      title: 'Custom IT Solutions',
      description: 'No cookie-cutter approaches. We create IT solutions tailored specifically to your business needs and budget.'
    },
    {
      icon: Database,
      title: 'Data Backup & Recovery',
      description: 'Expert data backup, disaster recovery, and management services to ensure your critical business data is always protected.'
    },
    {
      icon: Wifi,
      title: 'Network Setup & Support',
      description: 'Professional network installation, configuration, WiFi setup, and ongoing support to keep your team connected.'
    }
  ];

  return (
    <section className="py-16 lg:py-28 bg-muted/30" aria-labelledby="why-choose-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/optimized/pexels-5380664-sm.webp"
                srcSet="/images/optimized/pexels-5380664-xs.webp 400w, /images/optimized/pexels-5380664-sm.webp 600w"
                sizes="(max-width: 500px) 400px, 600px"
                width="600"
                height="400"
                alt="King of IT team providing professional computer support and IT services to Toronto businesses"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
            {/* Accent */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 sm:w-48 sm:h-48 bg-gradient-gold rounded-2xl -z-10 opacity-20" />
          </div>

          {/* Content */}
          <div className="space-y-6 order-1 lg:order-2">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              Why Toronto Businesses Choose King of IT
            </Badge>
            <h2 id="why-choose-heading" className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground">
              Your Complete IT Support Partner in{' '}
              <span className="text-gradient-gold">Toronto & GTA</span>
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              King of IT provides managed IT services tailored for small and mid-sized businesses. Our Toronto-based team delivers responsive support, vendor-neutral advice, and IT solutions that fit your budget. With 31 years in the IT industry, we've earned the trust of 500+ clients across the Greater Toronto Area.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              {benefits.slice(0, 4).map((benefit) => (
                <div key={benefit.title} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm sm:text-base">{benefit.title}</h3>
                    <p className="text-muted-foreground text-xs sm:text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link to="/about" data-testid="learn-more-about-btn">
              <Button className="bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90 mt-4">
                Learn More About King of IT
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Industries Section
const IndustriesSection = () => {
  // Industries with their links - some point to new dedicated landing pages
  const industries = [
    { name: 'Law Firms', path: '/law-firm-it-support-toronto' },
    { name: 'Medical Clinics', path: '/medical-practice-it-support-toronto' },
    { name: 'Dental Offices', path: '/industries/dental-offices' },
    { name: 'Accounting Firms', path: '/real-estate-accounting-it-support-toronto' },
    { name: 'Manufacturing', path: '/industries/manufacturing' },
    { name: 'Retail', path: '/industries/retail' },
    { name: 'Real Estate', path: '/real-estate-accounting-it-support-toronto' },
    { name: 'Non-Profits', path: '/industries/non-profits' },
    { name: 'Education', path: '/industries/education' },
    { name: 'Government', path: '/industries/government' }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background" aria-labelledby="industries-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 lg:mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            Industries We Serve in Toronto
          </Badge>
          <h2 id="industries-heading" className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground mb-4">
            IT Support for <span className="text-gradient-gold">Every Industry</span> in the GTA
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg mb-2">
            We have extensive experience providing IT services to businesses across Toronto and the Greater Toronto Area, understanding unique compliance requirements and technology needs for each industry.
          </p>
          <Link to="/industries" className="text-primary hover:underline font-medium text-sm">
            View all industries →
          </Link>
        </div>
        
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
          {industries.map((industry) => (
            <Link key={industry.name} to={industry.path} data-testid={`industry-${industry.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <Badge 
                variant="outline" 
                className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base border-primary/30 text-primary bg-primary/5 hover:border-primary hover:bg-primary hover:text-white transition-all cursor-pointer shadow-sm hover:shadow-md"
              >
                {industry.name}
              </Badge>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Review Submission Modal
const ReviewModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    rating: 5,
    review: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    // Load reCAPTCHA script
    if (isOpen && !window.grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.onload = () => setRecaptchaLoaded(true);
      document.body.appendChild(script);
    } else if (window.grecaptcha) {
      setRecaptchaLoaded(true);
    }
  }, [isOpen, RECAPTCHA_SITE_KEY]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      // Get reCAPTCHA token
      let recaptchaToken = '';
      if (window.grecaptcha && RECAPTCHA_SITE_KEY) {
        recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit_review' });
      }

      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          recaptcha_token: recaptchaToken
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: data.message });
        setFormData({ name: '', company: '', rating: 5, review: '' });
        setTimeout(() => {
          onClose();
          setMessage(null);
        }, 3000);
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to submit review' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-card border border-border rounded-xl shadow-2xl animate-fade-in-up">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-muted transition-colors"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <MessageSquarePlus className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">Leave a Review</h3>
            <p className="text-sm text-muted-foreground mt-1">Share your experience with King of IT</p>
          </div>

          {message && (
            <div className={`p-3 rounded-lg mb-4 ${message.type === 'success' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Your Name *</label>
              <input
                type="text"
                required
                minLength={2}
                maxLength={100}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Company *</label>
              <input
                type="text"
                required
                minLength={2}
                maxLength={100}
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                placeholder="Company Name, Toronto"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Rating *</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData({ ...formData, rating: star })}
                    className="focus:outline-none transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 ${star <= formData.rating ? 'text-primary fill-primary' : 'text-muted-foreground'}`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Your Review *</label>
              <textarea
                required
                minLength={10}
                maxLength={1000}
                rows={4}
                value={formData.review}
                onChange={(e) => setFormData({ ...formData, review: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none"
                placeholder="Tell us about your experience..."
              />
              <p className="text-xs text-muted-foreground mt-1">{formData.review.length}/1000 characters</p>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || !recaptchaLoaded}
              className="w-full bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Review'
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Your review will be published after admin approval.
              <br />
              Protected by reCAPTCHA.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Default testimonials (fallback)
  const defaultTestimonials = [
    {
      quote: "King of IT has been managing our law firm's network for 5 years. Their response time is incredible and they truly understand our business needs. Highly recommend their IT support services!",
      author: "Sarah M.",
      company: "Legal Firm, Toronto"
    },
    {
      quote: "When our server went down, King of IT had us back up and running within hours. Their emergency IT support is second to none. Best computer repair service in the GTA.",
      author: "Michael R.",
      company: "Medical Clinic, Markham"
    },
    {
      quote: "The team at King of IT helped us migrate to Microsoft 365 seamlessly. Our productivity has increased significantly. They're our go-to for all IT services in Toronto.",
      author: "Jennifer L.",
      company: "Accounting Firm, Vaughan"
    }
  ];

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reviews`);
        if (response.ok) {
          const data = await response.json();
          if (data.reviews && data.reviews.length > 0) {
            // Transform API reviews to match testimonial format
            const formattedReviews = data.reviews.map(r => ({
              quote: r.review,
              author: r.name,
              company: r.company,
              rating: r.rating
            }));
            setReviews(formattedReviews);
          }
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [API_URL]);

  // Use fetched reviews if available, otherwise use defaults
  const displayTestimonials = reviews.length > 0 ? reviews.slice(0, 6) : defaultTestimonials;

  return (
    <section className="py-16 lg:py-24 bg-muted/30" aria-labelledby="testimonials-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 lg:mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            Customer Reviews
          </Badge>
          <h2 id="testimonials-heading" className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground">
            What Toronto Businesses <span className="text-gradient-gold">Say About Us</span>
          </h2>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {displayTestimonials.map((testimonial, index) => (
            <Card key={index} className="p-6 lg:p-8 bg-card border-border/50" data-testid={`testimonial-${index}`}>
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating || 5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-primary fill-primary" />
                ))}
              </div>
              <blockquote className="text-muted-foreground mb-6 leading-relaxed text-sm sm:text-base">&ldquo;{testimonial.quote}&rdquo;</blockquote>
              <cite className="not-italic">
                <p className="font-semibold text-foreground">{testimonial.author}</p>
                <p className="text-sm text-muted-foreground">{testimonial.company}</p>
              </cite>
            </Card>
          ))}
        </div>

        {/* Leave a Review Button */}
        <div className="text-center mt-10">
          <Button
            onClick={() => setShowReviewModal(true)}
            className="bg-secondary text-white hover:bg-secondary/90 font-semibold"
            data-testid="leave-review-btn"
          >
            <MessageSquarePlus className="w-5 h-5 mr-2" />
            Leave a Review
          </Button>
        </div>
      </div>

      {/* Review Modal */}
      <ReviewModal isOpen={showReviewModal} onClose={() => setShowReviewModal(false)} />
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-16 lg:py-28 bg-background" aria-labelledby="cta-heading">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Crown className="w-12 h-12 sm:w-16 sm:h-16 text-primary mx-auto mb-4 sm:mb-6" />
        <h2 id="cta-heading" className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-foreground mb-4 sm:mb-6">
          Ready for Reliable <span className="text-gradient-gold">IT Support in Toronto?</span>
        </h2>
        <p className="text-muted-foreground text-base sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
          Get professional IT support and computer services for your Toronto business today. Whether you need computer repair, network setup, cybersecurity, or managed IT services, King of IT is here to help. <strong>Contact us for a free consultation</strong> – no obligation, just honest advice from Toronto's trusted IT experts.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <Link to="/contact" data-testid="cta-get-consultation">
            <Button 
              size="lg" 
              className="w-full sm:w-auto bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90"
            >
              Get Free IT Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <a href="tel:6479513653" data-testid="cta-call-now">
            <Button 
              size="lg" 
              variant="outline"
              className="w-full sm:w-auto border-secondary text-secondary hover:bg-secondary hover:text-white"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call 647-951-3653
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Main Homepage Component
const Homepage = () => {
  // NOTE: LocalBusiness schema with aggregateRating and reviews is defined ONCE in index.html
  // to avoid "Review has multiple aggregate ratings" error in Google Rich Results Test.
  // Only page-specific schemas (FAQ) are added here.

  // FAQ Schema for AI assistants - helps them answer common questions
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What IT services does King of IT provide in Toronto?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "King of IT provides comprehensive IT services including managed IT support ($99/user/month), cybersecurity (Secure365 from $15/user/month), network setup, Microsoft 365 migration, cloud services, backup & disaster recovery, and 24/7 emergency support. We serve businesses across Toronto and the GTA."
        }
      },
      {
        "@type": "Question",
        "name": "How fast is King of IT's response time?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "King of IT offers 15-minute response for critical issues, 1-hour response for standard requests, and on-site support within 2 hours across the Greater Toronto Area."
        }
      },
      {
        "@type": "Question",
        "name": "Does King of IT require long-term contracts?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, King of IT offers flexible month-to-month agreements with no long-term lock-in. You can cancel anytime with 30 days notice."
        }
      },
      {
        "@type": "Question",
        "name": "What areas does King of IT serve?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "King of IT serves all of Toronto and the Greater Toronto Area including Vaughan, Mississauga, Brampton, Markham, Richmond Hill, Newmarket, Aurora, Oakville, Burlington, Etobicoke, North York, and Scarborough."
        }
      },
      {
        "@type": "Question",
        "name": "How much does IT support cost in Toronto?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "King of IT's managed IT services start at $99/user/month. Cybersecurity (Secure365) starts at $15/user/month. Break-fix support is $150/hour. Contact us at 647-951-3653 for a custom quote."
        }
      },
      {
        "@type": "Question",
        "name": "Is King of IT PHIPA compliant for healthcare?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, King of IT specializes in PHIPA-compliant IT services for healthcare providers including medical offices, dental practices, and clinics. We support EMR systems like OSCAR, Accuro, and Telus Health."
        }
      },
      {
        "@type": "Question",
        "name": "Does King of IT support Mac computers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, King of IT supports both Windows and Mac environments, as well as mixed networks. Our technicians are certified in both platforms."
        }
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="King of IT | Toronto IT Support & Managed Services | 647-951-3653"
        description="King of IT - Toronto's trusted managed IT services company with 31 years of experience. Expert cybersecurity, Microsoft 365, cloud migration, and IT support for small and mid-sized businesses. 500+ clients served. Call 647-951-3653!"
        path="/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <CostCutterBanner />
      <main role="main" itemScope itemType="https://schema.org/WebPage">
        <HeroSection />
        <ServicesSection />
        <StatsSection />
        <WhyChooseUsSection />
        <IndustriesSection />
        <GoogleReviewsWidget />
        <CTASection />
      </main>
    </>
  );
};

export default Homepage;
